import {
  CheckCircle2,
  Check,
  Compass,
  Globe2,
  ShieldCheck,
  Target,
  UsersRound,
} from "lucide-react";
import CTA from "../components/public/CTA";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import SectionHeading from "../components/SectionHeading";
import { services } from "../data/services";

const objectives = [
  "Build trust through verified vendors and transparent processes.",
  "Enable diaspora clients to invest and transact safely in Nigeria.",
  "Provide end-to-end project execution.",
  "Offer secure payment and milestone tracking.",
  "Centralize multiple services under one brand.",
  "Scale into a leading managed services marketplace.",
];

const targetUsers = [
  {
    icon: UsersRound,
    title: "Individuals",
    users: [
      "Nigerians in the diaspora",
      "Local Nigerian customers",
      "Investors",
      "Busy professionals",
    ],
  },
  {
    icon: Globe2,
    title: "Businesses",
    users: ["Startups", "SMEs", "NGOs", "MSMEs"],
  },
  {
    icon: Target,
    title: "Internal users",
    users: [
      "Operations Administrators",
      "Finance Team",
      "Compliance Officers",
      "Customer Service Representatives",
    ],
  },
  {
    icon: ShieldCheck,
    title: "Service providers",
    users: [
      "Logistics companies",
      "Real estate agents",
      "Customs brokers",
      "Concierge agents",
      "Procurement specialists",
    ],
  },
];

export function About() {
  return (
    <>
      <PageHero
        eyebrow="About The Bridge by VA"
        title="Nigeria’s trusted marketplace for services that move life and business forward."
        copy="One trusted partner, from sourcing to delivery."
      />

      <section id="content" className="section-space bg-white">
        <div className="container">
          <div className="grid gap-12 border-y border-ink/10 py-16 lg:grid-cols-[.75fr_1.25fr] lg:gap-20 lg:py-20">
            <div>
              <p className="eyebrow">Who we are</p>
              <h2 className="mt-5 font-display text-4xl font-extrabold leading-tight tracking-[-.05em] text-ink sm:text-5xl">
                One platform for trusted services and managed execution.
              </h2>
            </div>
            <div>
              <p className="max-w-3xl text-lg leading-8 text-slate">
                The Bridge is a Nigerian technology-enabled services marketplace
                that connects individuals and businesses to the best offers on
                our services list.
              </p>
              <p className="mt-6 max-w-3xl text-lg leading-8 text-slate">
                The platform enables users to request and track high-value
                services across automobiles, logistics, clearing and forwarding,
                real estate, concierge, procurement management and regulatory
                compliance.
              </p>
              <p className="mt-6 max-w-3xl font-display text-xl font-bold leading-8 text-ink">
                The goal is to become the trusted execution partner for
                customers who want to invest, transact and manage projects in
                Nigeria with confidence.
              </p>
            </div>
          </div>

          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Reveal key={service.slug} delay={index * 50}>
                  <div className="h-full border-t-2 border-ocean/30 pt-5">
                    <Icon className="text-ocean" size={25} strokeWidth={2} />
                    <h3 className="mt-5 font-display text-lg font-bold text-ink">
                      {service.cardTitle}
                    </h3>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-space bg-ink text-white">
        <div className="container grid gap-12 lg:grid-cols-[.35fr_.65fr] lg:gap-20">
          <Reveal>
            <div className="grid h-14 w-14 place-items-center rounded-full border border-cyan/40 text-cyan">
              <Compass size={29} strokeWidth={1.8} />
            </div>
            <p className="mt-10 eyebrow text-cyan">Our vision</p>
          </Reveal>
          <Reveal delay={100}>
            <blockquote className="max-w-4xl font-display text-4xl font-extrabold leading-tight tracking-[-.05em] sm:text-6xl">
              “The Bridge is the Amazon for trusted services in Nigeria.”
            </blockquote>
            <p className="mt-7 max-w-2xl text-lg leading-8 text-slate-300">
              A single platform where customers can request, pay for and track
              verified services from start to finish.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="section-space bg-mist">
        <div className="container">
          <SectionHeading
            eyebrow="Our objectives"
            title="Built to make trust practical at scale."
            copy="The Bridge is designed around a clear set of business goals:"
            align="center"
          />
          <div className="mx-auto mt-12 grid max-w-5xl gap-x-12 gap-y-7 md:grid-cols-2">
            {objectives.map((objective, index) => (
              <Reveal key={objective} delay={index * 50}>
                <div className="flex gap-4 border-b border-ink/10 pb-6">
                  <CheckCircle2
                    className="mt-0.5 shrink-0 text-ocean"
                    size={22}
                  />
                  <p className="text-base leading-7 text-ink">{objective}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-space bg-white">
        <div className="container">
          <SectionHeading
            eyebrow="Who we serve"
            title="Designed for everyone who needs trusted execution."
            copy="The Bridge brings the people, organisations and operating roles around important work into one connected service experience."
            align="center"
          />
          <div className="mt-12 grid gap-8 border-y border-ink/10 py-8 sm:grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-ink/10">
            {targetUsers.map(({ icon: Icon, title, users }, index) => (
              <Reveal
                key={title}
                delay={index * 60}
                className="px-1 sm:px-4 lg:px-8 first:lg:pl-0 last:lg:pr-0"
              >
                <div>
                  <Icon className="text-ocean" size={28} strokeWidth={2} />
                  <h3 className="mt-5 font-display text-xl font-bold text-ink">
                    {title}
                  </h3>
                  <ul className="mt-5 space-y-3 text-sm leading-6 text-slate">
                    {users.map((user) => (
                      <li key={user} className="flex items-center gap-2">
                        <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-cyan text-ink">
                          <Check size={12} strokeWidth={3} aria-hidden="true" />
                        </span>
                        <span>{user}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTA />
    </>
  );
}
